import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Upload, ArrowLeft, Download, FileText, CheckCircle, AlertCircle,
  Users, Award, FileSpreadsheet
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

export const BulkGenerateCertificates = () => {
  const [templates, setTemplates] = useState([]);
  const [trainings, setTrainings] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [selectedTraining, setSelectedTraining] = useState('');
  const [generating, setGenerating] = useState(false);
  const [results, setResults] = useState(null);
  const { token } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchTemplates();
    fetchTrainings();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/certificates/templates?is_active=true`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (!response.ok) throw new Error('Failed to fetch templates');

      const data = await response.json();
      setTemplates(data.templates || []);

      const defaultTemplate = data.templates?.find(t => t.is_default);
      if (defaultTemplate) {
        setSelectedTemplate(defaultTemplate.id.toString());
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Failed to load templates');
    }
  };

  const fetchTrainings = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/training-calendar?is_active=true&limit=100`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (!response.ok) throw new Error('Failed to fetch trainings');

      const data = await response.json();
      setTrainings(data.trainings || []);
    } catch (error) {
      console.error('Error fetching trainings:', error);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
        toast.error('Please select a CSV file');
        return;
      }
      setSelectedFile(file);
      setResults(null);
    }
  };

  const downloadSampleCSV = () => {
    const sampleData = `name,email,course,training_date,duration,location
John Doe,john@example.com,ISO 9001 Lead Auditor Training,2025-12-20,5 Days,Bangalore
Jane Smith,jane@example.com,ISO 27001 Internal Auditor,2025-12-22,3 Days,Mumbai
Bob Johnson,bob@example.com,ISO 45001 Awareness Training,2025-12-25,2 Days,Delhi`;

    const blob = new Blob([sampleData], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sample_certificates.csv';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);

    toast.success('Sample CSV downloaded');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedFile) {
      toast.error('Please select a CSV file');
      return;
    }

    if (!selectedTemplate) {
      toast.error('Please select a template');
      return;
    }

    try {
      setGenerating(true);
      setResults(null);

      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('template_id', selectedTemplate);
      if (selectedTraining) {
        formData.append('training_id', selectedTraining);
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/certificates/generate/bulk`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        }
      );

      if (!response.ok) throw new Error('Failed to generate certificates');

      const data = await response.json();
      setResults(data);

      if (data.errors && data.errors.length > 0) {
        toast.warning(`${data.certificates.length} certificates generated with ${data.errors.length} errors`);
      } else {
        toast.success(`Successfully generated ${data.certificates.length} certificates!`);
      }
    } catch (error) {
      console.error('Error generating certificates:', error);
      toast.error('Failed to generate certificates');
    } finally {
      setGenerating(false);
    }
  };

  const resetForm = () => {
    setSelectedFile(null);
    setResults(null);
    const fileInput = document.getElementById('csv-file-input');
    if (fileInput) fileInput.value = '';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            onClick={() => navigate('/admin/certificates')}
            variant="outline"
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Bulk Generate Certificates</h1>
            <p className="text-gray-600 mt-1">Upload CSV to generate multiple certificates at once</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Form */}
        <div className="lg:col-span-2 space-y-6">
          {/* Upload Form */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Upload className="h-5 w-5" />
                Upload CSV File
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* File Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    CSV File *
                  </label>
                  <div className="flex items-center gap-4">
                    <label className="flex-1 cursor-pointer">
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-500 transition-colors">
                        {selectedFile ? (
                          <div>
                            <FileSpreadsheet className="h-12 w-12 text-green-600 mx-auto mb-2" />
                            <p className="text-sm font-medium text-gray-900">{selectedFile.name}</p>
                            <p className="text-xs text-gray-500 mt-1">
                              {(selectedFile.size / 1024).toFixed(2)} KB
                            </p>
                          </div>
                        ) : (
                          <div>
                            <Upload className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                            <p className="text-sm font-medium text-gray-900">Click to upload CSV</p>
                            <p className="text-xs text-gray-500 mt-1">or drag and drop</p>
                          </div>
                        )}
                      </div>
                      <input
                        id="csv-file-input"
                        type="file"
                        accept=".csv"
                        onChange={handleFileChange}
                        className="hidden"
                      />
                    </label>
                  </div>
                  <Button
                    type="button"
                    onClick={downloadSampleCSV}
                    variant="outline"
                    size="sm"
                    className="mt-3 gap-2"
                  >
                    <Download className="h-4 w-4" />
                    Download Sample CSV
                  </Button>
                </div>

                {/* Template Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Certificate Template *
                  </label>
                  <select
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={selectedTemplate}
                    onChange={(e) => setSelectedTemplate(e.target.value)}
                  >
                    <option value="">Select a template...</option>
                    {templates.map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.template_name}
                        {template.is_default ? ' (Default)' : ''}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Training Association (Optional) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Associate with Training (Optional)
                  </label>
                  <select
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={selectedTraining}
                    onChange={(e) => setSelectedTraining(e.target.value)}
                  >
                    <option value="">No association</option>
                    {trainings.map((training) => (
                      <option key={training.id} value={training.id}>
                        {training.title} - {new Date(training.date).toLocaleDateString()}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Optional: Link all certificates to a specific training event
                  </p>
                </div>

                {/* Submit Buttons */}
                <div className="flex gap-4">
                  <Button
                    type="submit"
                    disabled={generating || !selectedFile || !selectedTemplate}
                    className="bg-blue-600 hover:bg-blue-700 gap-2"
                  >
                    {generating ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Award className="h-4 w-4" />
                        Generate Certificates
                      </>
                    )}
                  </Button>
                  <Button
                    type="button"
                    onClick={resetForm}
                    variant="outline"
                  >
                    Reset
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Results */}
          {results && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  {results.errors?.length > 0 ? (
                    <AlertCircle className="h-5 w-5 text-amber-600" />
                  ) : (
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  )}
                  Generation Results
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Summary */}
                  <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                    <div>
                      <p className="text-sm text-gray-600">Batch ID</p>
                      <p className="font-mono font-medium text-gray-900">{results.batch_id}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Total Generated</p>
                      <p className="text-2xl font-bold text-green-600">{results.certificates?.length || 0}</p>
                    </div>
                  </div>

                  {/* Success List */}
                  {results.certificates && results.certificates.length > 0 && (
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-green-600" />
                        Successfully Generated ({results.certificates.length})
                      </h4>
                      <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50 sticky top-0">
                            <tr>
                              <th className="px-4 py-2 text-left">Row</th>
                              <th className="px-4 py-2 text-left">Name</th>
                              <th className="px-4 py-2 text-left">Certificate ID</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y">
                            {results.certificates.map((cert, index) => (
                              <tr key={index} className="hover:bg-gray-50">
                                <td className="px-4 py-2">{cert.row}</td>
                                <td className="px-4 py-2">{cert.name}</td>
                                <td className="px-4 py-2">
                                  <span className="font-mono text-blue-600">{cert.certificate_id}</span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Errors List */}
                  {results.errors && results.errors.length > 0 && (
                    <div>
                      <h4 className="font-medium text-red-900 mb-2 flex items-center gap-2">
                        <AlertCircle className="h-4 w-4 text-red-600" />
                        Errors ({results.errors.length})
                      </h4>
                      <div className="max-h-48 overflow-y-auto border border-red-200 rounded-lg bg-red-50">
                        <table className="w-full text-sm">
                          <thead className="bg-red-100 sticky top-0">
                            <tr>
                              <th className="px-4 py-2 text-left">Row</th>
                              <th className="px-4 py-2 text-left">Error</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-red-200">
                            {results.errors.map((error, index) => (
                              <tr key={index}>
                                <td className="px-4 py-2">{error.row}</td>
                                <td className="px-4 py-2 text-red-700">{error.error}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  <div className="flex gap-3 pt-4">
                    <Button
                      onClick={() => navigate('/admin/certificates')}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      View All Certificates
                    </Button>
                    <Button
                      onClick={resetForm}
                      variant="outline"
                    >
                      Generate More
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* CSV Format Guide */}
          <Card className="bg-blue-50 border-blue-200">
            <CardHeader>
              <CardTitle className="text-blue-900">CSV Format Guide</CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-blue-800 space-y-3">
              <div>
                <h4 className="font-medium mb-2">Required Columns:</h4>
                <ul className="space-y-1 ml-4">
                  <li>• <code className="bg-blue-100 px-1 rounded">name</code> - Participant's full name</li>
                  <li>• <code className="bg-blue-100 px-1 rounded">email</code> - Email address</li>
                  <li>• <code className="bg-blue-100 px-1 rounded">course</code> - Course name</li>
                </ul>
              </div>

              <div>
                <h4 className="font-medium mb-2">Optional Columns:</h4>
                <ul className="space-y-1 ml-4">
                  <li>• <code className="bg-blue-100 px-1 rounded">training_date</code> - YYYY-MM-DD</li>
                  <li>• <code className="bg-blue-100 px-1 rounded">duration</code> - e.g., "5 Days"</li>
                  <li>• <code className="bg-blue-100 px-1 rounded">location</code> - e.g., "Bangalore"</li>
                </ul>
              </div>

              <div className="pt-2 border-t border-blue-300">
                <h4 className="font-medium mb-2">Example Row:</h4>
                <pre className="bg-blue-100 p-2 rounded text-xs overflow-x-auto">
John Doe,john@example.com,
ISO 9001 Training,2025-12-20,
5 Days,Bangalore
                </pre>
              </div>
            </CardContent>
          </Card>

          {/* Stats Card */}
          {results && (
            <Card>
              <CardHeader>
                <CardTitle>Statistics</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span className="text-sm font-medium text-green-900">Success</span>
                  </div>
                  <span className="text-2xl font-bold text-green-600">
                    {results.certificates?.length || 0}
                  </span>
                </div>

                {results.errors && results.errors.length > 0 && (
                  <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="h-5 w-5 text-red-600" />
                      <span className="text-sm font-medium text-red-900">Errors</span>
                    </div>
                    <span className="text-2xl font-bold text-red-600">
                      {results.errors.length}
                    </span>
                  </div>
                )}

                <div className="pt-3 border-t">
                  <p className="text-xs text-gray-500">
                    All certificates are tagged with batch ID: <span className="font-mono">{results.batch_id}</span>
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Tips */}
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="p-4">
              <h4 className="font-medium text-amber-900 mb-2">💡 Tips</h4>
              <ul className="text-sm text-amber-800 space-y-1">
                <li>• Download sample CSV to see the correct format</li>
                <li>• Ensure all required columns are present</li>
                <li>• Dates should be in YYYY-MM-DD format</li>
                <li>• All certificates get unique IDs</li>
                <li>• Errors won't stop the whole batch</li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
